// src/utils/buildVerticalLayout.ts

import { AppNode } from "../Modules/Admin/ReactFlow/OptionPairGraph";

/**
 * Represents the position of a node.
 */
export interface NodePosition {
   x: number;
   y: number;
}

/**
 * Represents the layout data returned by buildVerticalLayout.
 */
export interface LayoutData {
   layoutPositions: Record<number, NodePosition>;
   incomingEdgesMap: Record<number, number[]>;
}

/**
 * Builds a (mostly) vertical DAG layout for the graph using a topological sort.
 * @param graph - Record mapping nodeId to AppNode.
 * @returns LayoutData containing positions and incoming edges map.
 */
export function buildVerticalLayout(
   graph: Record<number, AppNode>
): LayoutData {
   const incomingEdgesMap: Record<number, number[]> = {};
   const inDegree: Record<number, number> = {};

   // Initialize incoming edges + inDegree count
   Object.values(graph).forEach((node) => {
      inDegree[node.id] = 0;
      incomingEdgesMap[node.id] = [];
   });

   // Fill in-degree and incomingEdgesMap
   Object.values(graph).forEach((node) => {
      node.outgoingEdges?.forEach((edge) => {
         const targetId = edge.targetNodeId;
         // Record the parent->child in the incomingEdgesMap
         incomingEdgesMap[targetId].push(node.id);
         // Increment in-degree
         inDegree[targetId] = (inDegree[targetId] || 0) + 1;
      });
   });

   // Kahn's Algorithm for topological sort
   const queue: number[] = [];
   // Enqueue nodes with inDegree = 0
   Object.keys(inDegree).forEach((nodeIdStr) => {
      const nodeId = Number(nodeIdStr);
      if (inDegree[nodeId] === 0) {
         queue.push(nodeId);
      }
   });

   const topoOrder: number[] = [];

   while (queue.length > 0) {
      const current = queue.shift()!;
      topoOrder.push(current);

      // Decrement inDegree of all children
      graph[current].outgoingEdges?.forEach((edge) => {
         const target = edge.targetNodeId;
         inDegree[target] -= 1;
         if (inDegree[target] === 0) {
            queue.push(target);
         }
      });
   }

   // If topoOrder.length < number of nodes, there might be a cycle
   // (For a real robust solution, handle that accordingly.)
   // For now, assume no cycles or they won't appear in a perfect layout.

   // Compute each node's level = 1 + max(level of parents)
   const levelMap: Record<number, number> = {};
   // Initialize all to 0
   topoOrder.forEach((nodeId) => {
      levelMap[nodeId] = 0;
   });

   topoOrder.forEach((nodeId) => {
      // Parents in the incomingEdgesMap
      const parents = incomingEdgesMap[nodeId];
      if (parents.length === 0) {
         // root node
         levelMap[nodeId] = 0;
      } else {
         // level = 1 + max(level(parents))
         levelMap[nodeId] =
            1 + Math.max(...parents.map((p) => levelMap[p] ?? 0));
      }
   });

   // Group nodes by level
   const levels: Record<number, number[]> = {};
   Object.entries(levelMap).forEach(([nodeIdStr, lvl]) => {
      const nodeId = Number(nodeIdStr);
      if (!levels[lvl]) {
         levels[lvl] = [];
      }
      levels[lvl].push(nodeId);
   });

   // Layout positions
   // We'll space out levels by 200 vertically and siblings by 200 horizontally
   const layoutPositions: Record<number, NodePosition> = {};

   // Sort levels so they go from 0..max
   const sortedLevelKeys = Object.keys(levels)
      .map(Number)
      .sort((a, b) => a - b);

   sortedLevelKeys.forEach((lvl) => {
      const nodesAtLevel = levels[lvl];
      // Sort them in the order they appear in topoOrder (purely for consistency)
      nodesAtLevel.sort((a, b) => topoOrder.indexOf(a) - topoOrder.indexOf(b));

      nodesAtLevel.forEach((nodeId, i) => {
         layoutPositions[nodeId] = {
            x: i * 200,
            y: lvl * 200,
         };
      });
   });

   return { layoutPositions, incomingEdgesMap };
}
